<template>
  <main class="login">
    <div class="login-container">
      <div class="login-block">
        <div class="login-block__logo">
          <Icon :icon="'logo'" />
        </div>

        <div v-show="!resetPassword && !passwordUpdateRequired" class="login-block__form">
          <el-form
            ref="ruleForm"
            :model="ruleForm"
            :rules="rules"
            :hide-required-asterisk="true"
            @submit.native.prevent="submitForm('ruleForm')"
          >
            <el-form-item
              :label="
                rememberPassword
                  ? 'Email address'
                  : 'Please enter your Email to receive a link to reset your Password'
              "
              prop="email"
            >
              <el-input v-model="ruleForm.email" type="text" />
            </el-form-item>

            <el-form-item
              v-if="rememberPassword"
              label="Password"
              prop="password"
            >
              <el-input
                v-model="ruleForm.password"
                type="password"
                autocomplete="off"
              />
            </el-form-item>

            <div v-if="serverError" class="server-error" v-html="serverError">
            </div>

            <el-form-item>
              <el-button type="primary" native-type="submit" :loading="isSubmitting">
                {{ rememberPassword ? "Login" : "Submit" }}
              </el-button>
            </el-form-item>
          </el-form>
        </div>

        <div v-show="resetPassword" class="reset-block__form">
          <el-form
            ref="ruleResetForm"
            :model="ruleResetForm"
            :rules="resetRules"
            :hide-required-asterisk="true"
            @submit.native.prevent="submitForm('ruleResetForm')"
          >
            <el-form-item
              v-show="!resetPassword"
              :label="
                rememberPassword
                  ? 'Email address'
                  : 'Please enter your Email to receive a link to reset your Password'
              "
              prop="email"
            >
              <el-input v-model="ruleForm.email" type="text" />
            </el-form-item>

            <el-form-item
              v-if="resetPassword"
              label="New Password"
              prop="password"
            >
              <el-input
                v-model="ruleResetForm.password"
                type="password"
                autocomplete="off"
              />
            </el-form-item>

            <el-form-item
              v-if="resetPassword"
              label="Confirm Password"
              prop="confirmPassword"
            >
              <el-input
                v-model="ruleResetForm.confirmPassword"
                type="password"
                autocomplete="off"
              />
            </el-form-item>

            <div v-if="serverError" class="server-error" v-html="serverError">
            </div>

            <el-form-item>
              <el-button type="primary" native-type="submit" :loading="isSubmitting">
                {{ resetPassword ? "Submit" : "Login" }}
              </el-button>
            </el-form-item>
          </el-form>
        </div>

        <!-- Password Update Form -->
        <div v-if="passwordUpdateRequired" class="password-update-block">

          <div class="el-form-item__label short-password-notice">
            Short password detected - your password must be at least 6 characters long.
            Please change your password to continue:
          </div>

          <el-form
            ref="passwordUpdateForm"
            :model="ruleUpdateForm"
            :rules="updateRules"
            @submit.native.prevent="submitPasswordUpdateForm"
          >
            <el-form-item label="Current Password" prop="currentPassword">
              <el-input v-model="ruleUpdateForm.currentPassword" type="password" autocomplete="off" />
            </el-form-item>

            <el-form-item label="New Password" prop="newPassword">
              <el-input v-model="ruleUpdateForm.newPassword" type="password" autocomplete="off" />
            </el-form-item>

            <el-form-item label="Confirm New Password" prop="confirmNewPassword">
              <el-input v-model="ruleUpdateForm.confirmNewPassword" type="password" autocomplete="off" />
            </el-form-item>

            <div v-if="serverError" class="server-error" v-html="serverError">
            </div>

            <el-form-item>
              <el-button
                type="primary"
                native-type="submit"
                :disabled="!updateFormValid"
                :loading="isSubmitting"
              >
                Update Password
              </el-button>
            </el-form-item>
          </el-form>
        </div>
        <div v-if="!resetPassword && !passwordUpdateRequired" class="login-block__link">
          <p @click="forgotPassword">Forgot password?</p>
        </div>
      </div>

      <div class="login-info">
        <div class="login-info__content">
          <div class="login-info__title">
            <TitleBlock
              text="PC Getting started"
              :color="'brightblue'"
              :size="'small'"
              :type="'alternative'"
              :padding="false"
            />
          </div>

          <div class="login-info__text">
            <div class="login-info__text-title">Existing subscribers</div>
            <p>
              You should have received an email with a new username and
              password. If you haven’t,
              <span @click="forgotPassword">click here</span> to request a new
              one, or contact your Account Manager.
            </p>
            <div
              class="login-info__text-title login-info__text-title--margin-top"
            >
              Not a subscriber?
            </div>
            <p>
              <a
                href="https://www.vixio.com/request-a-demo"
                target="_blank"
                >Click here</a
              >
              to learn more about VIXIO PaymentsCompliance
            </p>
          </div>

          <div class="login-info__text">
            <div class="login-info__text-title">
              How can our Horizon Scanning solution help you?
            </div>
            <p>
              Get access to a comprehensive, personalised feed of the latest
              regulatory updates, sourced daily from thousands of regulatory
              bodies across multiple jurisdictions and languages.
            </p>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { mapState } from "vuex";
import setTokens from "../utils/setTokens";
import { routeAccess } from "@/config";
import logout from "~/utils/logout";

export default {
  name: "LoginPage",

  components: {
    Icon: () => import("../components/elements/Icon"),
    TitleBlock: () => import("../components/elements/TitleBlock"),
  },
  layout: "full",

  data() {
    return {
      serverError: null,

      ruleForm: {
        email: "",
        password: "",
      },

      ruleResetForm: {
        password: "",
        confirmPassword: "",
      },

      rules: {
        email: [
          {
            required: true,
            message: "Please enter Email address",
            trigger: "blur",
          },
        ],
        password: [
          { required: true, message: "Please enter password", trigger: "blur" },
        ],
        confirmPassword: [
          {
            required: true,
            message: "Please confirm password",
            trigger: "blur",
          },
        ],
      },

      resetRules: {
        password: [
          { required: true, message: "Please enter password", trigger: "blur" },
        ],
        confirmPassword: [
          {
            required: true,
            message: "Please confirm password",
            trigger: "blur",
          },
        ],
      },

      loadingData: false,
      rememberPassword: true,
      isSubmitting: false,

      // CI-39: Change of password for Cognito users where password length < 6 chars in length.
      passwordUpdateRequired: false,

      updateFormValid: false,

      ruleUpdateForm: {
        email: "",
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
      },

      updateRules: {
        currentPassword: [
          { required: true, message: "Please enter your current password", trigger: "blur" },
        ],
        newPassword: [
          {
            required: true,
            message: "Please enter a new password",
            trigger: "blur"
          },
          {
            min: 6,
            message: "New password must be at least 6 characters",
            trigger: "blur",
          },
          {
            validator: (rule, value, callback) => {
              if (value === this.ruleUpdateForm.currentPassword) {
                callback(new Error("New password cannot be the same as current password"));
              } else {
                callback();
              }
            },
            trigger: "blur",
          }
        ],
        confirmNewPassword: [
          { required: true, message: "Please confirm your new password", trigger: "blur" },
          {
            validator: (rule, value, callback) => {
              if (value !== this.ruleUpdateForm.newPassword) {
                callback(new Error("The passwords do not match"));
              } else {
                callback();
              }
            },
            trigger: "blur",
          }
        ],
      },
    };
  },
  head() {
    return { title: "VIXIO PaymentsCompliance" };
  },
  computed: {
    ...mapState("Auth", ["resetPassword", "subscriptions"]),
  },
  // CI-39: Change of password for Cognito users where password length < 6 chars in length.
  watch: {
    ruleUpdateForm: {
      deep: true,
      handler() {
        if (this.$refs.passwordUpdateForm) {
          this.$refs.passwordUpdateForm.validate((valid) => {
            this.updateFormValid = valid;
          });
        }
      },
    },
  },
  methods: {
    forgotPassword() {
      this.rememberPassword = false;
      this.serverError = null;

      // this.$store.commit("Auth/setData", {
      //   label: "resetPassword",
      //   data: true,
      // });
    },
    submitForm(formName) {
      this.isSubmitting = true;
      if (!this.resetPassword) {
        this.$refs[formName].validate((valid) => {
          if (valid) {
            this.rememberPassword ? this.login() : this.getNewPassword();
          }
        });
      } else {
        this.setNewPassword();
      }
      setTimeout(() => {
        this.isSubmitting = false;
      }, 2000);
    },

    async login() {
      this.loadingData = true;
      this.serverError = null;
      await this.$store
        .dispatch("Auth/token", {
          username: this.ruleForm.email,
          password: this.ruleForm.password,
        })
        .then(async (response) => {
          const status = response.statusCode;
          const data = JSON.parse(response.data);
          if (status !== 200) {
            // CI-39: Change of password for congito users where password length < 6 chars in length.
            const data = JSON.parse(response.data);
            if (data.hint === "PASSWORD_UPDATE_REQUIRED") {
              this.showPasswordUpdateForm();
              // Cognito user has registered with another platform.
            } else if (data.hint === "COGNITO_PASSWORD_MISMATCH") {
              this.serverError = `
                Due to Vixio implementing a single sign-on service, we have detected that you have already enrolled on another Vixio platform.</br></br>
                Please log in using that email address and password, or you can reset your password below. If you reset your password, it should be used on all Vixio platforms.
              `;
            } else {
              this.serverError = "Incorrect Email address or Password entered";
              this.loadingData = false;
            }
          } else {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: "login",
              login_success: "true"
            });
            await setTokens(
              this,
              data.access_token,
              data.refresh_token,
              data.expires_in,
              data.token_type,
              data.id_token,
            );
            await this.$store.commit("Auth/setData", {
              label: "isAuthorize",
              data: true,
            });
            await this.$store.commit("Auth/setToken", data);
            // this.$axios({
            //   method: "GET",
            //   url: `${this.$config.baseURL}/api/user-permissions`,
            //   headers: {
            //     "Content-Type": "application/json",
            //     Authorization: `Bearer ${this.$store.state.Auth.access_token}`,
            //   },
            // })
            //   .then((data) => {
            //     if (data.data) {
            //       this.$store.commit("Auth/setPermissions", data.data);
            //     }
            //   })
            //   .catch((error) => console.error(error));
            let subs = null;
            let active = false;
            await this.$axios({
              method: "GET",
              url: `${this.$config.baseURL}/api/user-subscriptions`,
              headers: {
                "Content-Type": "application/json"
              }
            })
              .then((data) => {
                if (data.data) {
                  active = data.data.active;
                  this.$store.commit("Auth/setData", { label: "subscriptions", data: data.data });
                  subs = data.data.permissions;
                }
              })
              .catch((error) => console.error(error));
            if (!active) {
                this.$router.push("/login");
                logout(this);
                this.serverError = "You have no active subscription. Please contact your account manager.";
                this.loadingData = false;
                return;
            }
            const path = this.$cookies.get("pastUrl");

            const requiredPermissions = routeAccess
              .filter(el => path && path.startsWith(el.path))
              .map(el => el.permission);

            const userHasPermission = requiredPermissions.length === 0 ||
              requiredPermissions.filter(el => subs.includes(el))
              .length > 0;

            if (path && userHasPermission && path !== "/login" && path !== "/reset-password") {
              this.$router.replace({ path });
            } else {
              this.$router.replace({ path: "/" });
            }
            this.loadingData = false;
            // this.$router.replace({path: "/"});
            await this.$store
              .dispatch("Profile/getData", {
                url: "/user",
                token: this.$store.state.Auth.access_token,
              })
              .then(({ data }) => {
                if (
                  this.$matomo &&
                  data.salesforceId &&
                  data.salesforceId.length
                ) {
                  this.$matomo.setUserId(data.salesforceId);
                }
                this.$store.commit("Profile/setData", {
                  label: "dataUser",
                  data,
                });
                // const xhttp = new XMLHttpRequest();
                // xhttp.open("GET", "https://go.vixio.com/l/77152/2020-08-20/9lw46p?email=" + this.ruleForm.email, true);
                // xhttp.send()
                // this.$axios({
                //     method: "GET",
                //     url: `${config.env.BASE_URL}/api/user/subscription/active/id`,
                //     headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                // })
                //     .then((data) => {
                //         if (data) this.$store.commit("Profile/setData", { label: "userAccess", data: data.data });
                //         const path = this.$cookies.get("pastUrl")
                //         if (path && path !== "/login" && path !== "/reset-password") {
                //             this.$router.replace({ path });
                //         } else {
                //             this.$router.replace({ path: "/" });
                //         }
                //         this.loadingData = false;
                //     })
                //     .catch(error => console.error(error));
              })
              .catch((error) => {
                this.$sentry.captureException(new Error(error));
                console.error(error);
              });
          }
        })
        .catch((error) => {
          this.loadingData = false;
          console.error(error);
        });
    },
    // CI-39: Change of password for congito users where password length < 6 chars in length.
    showPasswordUpdateForm() {
      this.passwordUpdateRequired = true;
      this.ruleUpdateForm.email = this.ruleForm.email;
      this.ruleUpdateForm.currentPassword = this.ruleForm.password;
      this.ruleForm.email = "";
      this.ruleForm.password = "";
    },
    // This code is obselete and came across from the GC project
    getNewPassword() {
      this.loadingData = true;
      this.serverError = null;
      const data = {
        mail: this.ruleForm.email,
      };
      this.$axios({
        method: "POST",
        url: `${this.$config.baseURL}/user/lost-password-pardot?_format=json`,
        headers: { "Content-Type": "application/json" },
        data,
      })
        .then((data) => {
          this.loadingData = false;
          if (data.status === 400) {
            this.serverError = data.data.message;
          } else {
            this.$alert(
              "A Password reset Email has been sent to the Email you have entered, please check",
              {
                confirmButtonText: "Ok",
              }
            );
            this.ruleForm.email = "";
            this.ruleForm.password = "";
            this.rememberPassword = true;
          }
        })
        // .catch((error) => {   //SHould I just remove the error param like this?
        .catch(() => {
          this.loadingData = false;
        });
    },
    setNewPassword() {
      this.loadingData = true;
      this.serverError = null;
      const name = this.$route.query.user;
      const token = this.$route.query.token;
      const data = {
        name,
        temp_pass: token,
        new_pass: this.ruleResetForm.password,
      };
      this.$axios({
        method: "POST",
        url: `${this.$config.baseURL}/user/lost-password-reset?_format=json`,
        data,
      })
        .then((data) => {
          if (data.status === 400) {
            this.serverError = data.data.message;
          } else {
            this.$store.commit("Auth/setData", {
              label: "resetPassword",
              data: false,
            });
            this.$store.commit("Auth/setData", {
              label: "resetPasswordUser",
              data: null,
            });
            this.$store.commit("Auth/setData", {
              label: "resetPasswordToken",
              data: null,
            });
            this.$router.replace({ path: "/login" });
          }
          this.loadingData = false;
        })
        .catch((error) => {
          this.loadingData = false;
          console.error(error);
        });

      this.$store.commit("Auth/setData", {
        label: "resetPassword",
        data: true,
      });
    },
    // CI-39: Change of password for congito users where password length < 6 chars in length.
    submitPasswordUpdateForm() {
      this.isSubmitting = true;
      this.serverError = null;

      this.$refs.passwordUpdateForm.validate((valid) => {
        if (valid) {
          const payload = {
            email: this.ruleUpdateForm.email,
            old_password: this.ruleUpdateForm.currentPassword,
            new_password: this.ruleUpdateForm.newPassword,
          };

          this.$axios({
            method: "POST",
            url: `${this.$config.baseURL}/api/payments_compliance/cognito/password-update`,
            headers: {
              Authorization: "Basic Og==",
              "Content-Type": "application/json",
            },
            data: payload,
          })
            .then((response) => {
              if (response.status === 200 && response.data.status === "PASSWORD_UPDATED") {
                this.$alert("Password updated successfully. Please log in with your new password.", {
                  confirmButtonText: "Ok",
                  callback: () => {
                    // Reset form fields
                    this.ruleForm.email = "";
                    this.ruleForm.password = "";
                    this.ruleUpdateForm.currentPassword = "";
                    this.ruleUpdateForm.newPassword = "";
                    this.ruleUpdateForm.confirmNewPassword = "";

                    // Show login form again
                    this.passwordUpdateRequired = false;
                    this.rememberPassword = true;
                  },
                });
              } else {
                this.serverError = response.data.message || "Failed to update password.";
              }
            })
            .catch((error) => {
              console.error("Error updating password:", error);
              this.serverError = error.response?.data?.message || "An error occurred. Please try again.";
            })
            .finally(() => {
              this.isSubmitting = false;
            });
        } else {
          // If validation failed, reset isSubmitting after 2s (to mimic existing flow)
          setTimeout(() => {
            this.isSubmitting = false;
          }, 2000);
        }
      });
    },
  },
};
</script>

<style lang="scss">
@import "../assets/styles/helpers/variables";
@import "../assets/styles/helpers/mixin";

.login {
  min-height: 100vh;
  position: relative;
  background-image: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)),
    url(../assets/images/login/VIXIO__Login_animated.gif);
  background-position: right center;
  background-color: $corporateBlue;
  background-size: cover;
  display: flex;
  align-items: center;

  &-container {
    width: 100%;
    padding: 0 2rem;
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    justify-content: center;

    @include below($md) {
      flex-direction: column;
      align-items: center;
      margin-top: 3rem;
      padding: 5rem 2rem;
    }
  }

  &-block {
    background-color: $white;
    width: 100%;
    max-width: 45rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 3rem 4rem;
    position: relative;

    /* @include above($lg) {
      left: 50%;
      transform: translateX(-50%);
    } */

    &__logo {
      margin-bottom: 3.4rem;
      width: 50%;
    }

    &__form {
      width: 100%;

      .el-form {
        &-item {
          margin-bottom: 2rem;

          &__label {
            text-align: left;
            font-size: 1.6rem;
            line-height: 2rem;
            font-weight: 600;
            color: $text_gray;
            margin-bottom: 0.5rem;
          }

          &__content {
            .el-input {
              font-size: 1.8rem;
              line-height: 2.4rem;

              &__inner {
                height: 4.8rem;
                padding: 1rem 2rem;
                border-radius: 0.4rem;
                background-color: #e8f0fe;
                color: $text_gray;
                border: 1px solid #e8f0fe;
              }
            }

            .el-button {
              margin: 0.5rem auto 0;
              display: flex;
              background-color: $loginButton;
              border-color: darken($loginButton, 40%);
              font-size: 1.8rem;
              line-height: 2.4rem;
              font-weight: 700;
              padding: 0.8rem 3rem;

              &:hover {
                background-color: $lighterBrightGreen;
                color: $red;
              }
            }
          }

          &__error {
            color: $validation-error;
          }

          &.is-error {
            .el-input__inner {
              border-color: $validation-error;
            }

            .el-form-item__label {
              color: $validation-error;
            }
          }
        }
      }
    }

    &__link {
      margin-top: 1rem;

      p {
        position: relative;
        color: $text;
        font-size: 1.4rem;
        font-weight: 600;
        text-decoration: none;
        cursor: pointer;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }

  &-info {
    width: 100%;
    max-width: 30rem;
    position: relative;
    margin-left: 2rem;

    /* @include above($lg) {
      left: 50%;
      transform: translateX(-50%);
    } */

    @include between($md, $lg) {
      margin-left: 3.4rem;
    }

    @include below(874px) {
      margin-top: 3rem;
      max-width: 45rem;
      margin-left: 0;
    }

    &__content {
      position: relative;
      height: 100%;
    }

    &__text {
      margin-bottom: 1rem;
      background: $lighterBrightBlue;
      padding: 2rem 2.5rem;

      &:last-child {
        margin-bottom: 0;
      }

      &-title {
        font-size: 1.6rem;
        line-height: 2rem;
        font-weight: 600;
        // margin-bottom: 0.5rem;
        color: $black;

        &--margin-top {
          margin-top: 2rem;
        }
      }

      p {
        color: $black;
        font-size: 1.4rem;
        margin: 0;

        a,
        span {
          color: $red;
          text-decoration: none;

          &:hover {
            text-decoration: underline;
          }
        }
      }
    }

    &__title {
      margin-bottom: 0;

      /*       .title-block__text {
              background: $gold;
            } */
    }
  }
}

.server-error {
  color: $errorRed;
  text-align: center;
  font-size: 1.4rem;
  margin-bottom: 2rem;
}
// CI-39: Change of password for congito users where password length < 6 chars in length.
.password-update-block {
  .short-password-notice {
    margin-bottom: 3.0rem !important;
  }
  .el-form-item__label {
    text-align: center;
    line-height: 2rem;
    font-size: 1.6rem !important;
    font-weight:600;
    color: #262a2e;
    margin-bottom: 0.5rem;
  }
}
</style>
